import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Badge } from "@shopify/polaris";
import { RootState, AppDispatch } from "configureStore";
import { fetchOnboardingsThunk, setFilterValue } from "./onboardings.slice";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import {
  getOnboardingStatusTone,
  getOnboardingStatusLabel,
} from "utils/onboardingStatus";
import {
  getJobLeaveStatusLabel,
  getJobLeaveStatusTone,
} from "utils/jobLeaveStatus";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import {
  canLeadHiring as getCanLeadHiring,
  canManageOnboarding as getCanManageOnboarding,
} from "utils/onboardingPermissions";
import PersonalasTabs from "features/positions/PersonalasTabs";

function resolveLeaveStatus(
  item: any,
  t: (k: string) => string,
  lang: "lt" | "en"
) {
  if (item.jobLeavePhase === "active" && item.jobLeaveStatus) {
    return {
      label: getJobLeaveStatusLabel(item.jobLeaveStatus, t, lang),
      tone: getJobLeaveStatusTone(item.jobLeaveStatus),
    };
  }
  if (item.jobLeavePhase === "completed") {
    return {
      label: t("left_company"),
      tone: "critical" as const,
    };
  }
  return null;
}

function Onboardings() {
  const dispatch: AppDispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const { companyId } = useParams();

  const { collection, filtersValue, isLoading } = useSelector(
    (state: RootState) => state.onboardingsReducer
  );

  useEffect(() => {
    if (companyId) dispatch(fetchOnboardingsThunk(companyId, filtersValue));
  }, [dispatch, filtersValue, companyId]);

  const handleQueryChange = (value: string) => {
    dispatch(setFilterValue({ key: "textSearch", value }));
  };
  const handleQueryValueRemove = () => {
    dispatch(setFilterValue({ key: "textSearch", value: "" }));
  };

  const { role } = useSelector((state: RootState) => state.authReducer);
  const canManageOnboarding = getCanManageOnboarding(role);
  const canLeadHiring = getCanLeadHiring(role);

  const tableData =
    collection?.map((item: any, idx: number) => {
      const leave = resolveLeaveStatus(item, t, lang);
      return {
        id: item.id,
        "#": idx + 1,
        employeeName: `${item.firstName} ${item.lastName}`,
        positionLT: item.positionLT || "-",
        departments:
          (item.departments || []).map((d: { name: string }) => d.name).join(", ") ||
          "-",
        hiringDate: item.hiringDate,
        status:
          item.mode === "contract_registration"
            ? t("contract_registration")
            : getOnboardingStatusLabel(item.status, t, lang),
        tone:
          item.mode === "contract_registration"
            ? "info"
            : getOnboardingStatusTone(item.status),
        leaveStatus: leave?.label || "-",
        leaveTone: leave?.tone || null,
        originalContractNumber: item.originalContractNumber || "-",
        managerName:
          typeof item.manager === "object"
            ? item.manager?.name
            : item.manager || "-",
        createdByName:
          typeof item.createdBy === "object"
            ? item.createdBy?.name
            : item.createdBy,
        createdAt: item.createdAt,
      };
    }) || [];

  // The whole row (table row on a tablet or computer, card on a phone) opens
  // the record, so no cell needs its own click handler.
  const onboardingUrl = (item: any) =>
    `/app/${companyId}/onboarding/${item.id}`;

  const handleCreate = () => {
    dispatch(setModalxComponent("OnboardingForm"));
    dispatch(setModalxComponentProps({}));
    dispatch(setIsModalxOpen(true));
  };

  const columns = [
    { key: "#" },
    {
      key: "employeeName",
      name: t("employee"),
      url: onboardingUrl,
      mobileView: "topLeft",
    },
    {
      key: "positionLT",
      name: t("position"),
      mobileView: "bottomLeft",
    },
    {
      key: "departments",
      name: t("departments"),
    },
    {
      key: "originalContractNumber",
      name: t("original_contract_number"),
    },
    {
      key: "hiringDate",
      name: t("hiring_date"),
      type: "clickabledate",
      mobileView: "bottomRight",
    },
    {
      key: "status",
      name: t("onboarding_status"),
      type: "badge",
      mobileView: "topRight",
    },
    {
      key: "leaveStatus",
      name: t("employment_leave"),
      type: "custom",
      render: (item: any) => {
        if (!item.leaveTone || item.leaveStatus === "-") {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <Badge
            tone={
              item.leaveTone === "success" ||
              item.leaveTone === "critical" ||
              item.leaveTone === "attention" ||
              item.leaveTone === "warning"
                ? item.leaveTone
                : "info"
            }
          >
            {item.leaveStatus}
          </Badge>
        );
      },
    },
    {
      key: "managerName",
      name: t("manager"),
    },
    {
      key: "createdByName",
      name: t("created_by"),
    },
    {
      key: "createdAt",
      name: t("created_at"),
      type: "clickabledate",
    },
  ] as Column[];

  return (
    <div className="w-full px-6 py-4">
      {companyId && <PersonalasTabs companyId={companyId} />}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold">{t("hr_module")}</h1>
          {canManageOnboarding && <AuditLogPopover subjectType="Onboarding" />}
        </div>
        {canLeadHiring && (
          <button
            className="px-4 py-2 max-md:min-h-11 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            onClick={handleCreate}
          >
            {t("create_onboarding")}
          </button>
        )}
      </div>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-4">
          <input
            type="text"
            className="w-full px-3 py-2 max-md:min-h-11 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t("search") + "..."}
            value={filtersValue.textSearch || ""}
            onChange={(e) => handleQueryChange(e.target.value)}
          />
        </div>
        {isLoading && tableData.length === 0 ? (
          <DataTableSkeleton rows={10} columns={columns.length} />
        ) : (
          <DataTable
            collection={tableData}
            columns={columns}
            rowUrl={onboardingUrl}
          />
        )}
      </div>
    </div>
  );
}

export default Onboardings;
